import { faGithub, faLinkedin, faTwitter } from '@fortawesome/free-brands-svg-icons';
import { faEnvelope, faLocationDot, faPhone } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import React, { useRef, useState } from 'react';
import ReCAPTCHA from "react-google-recaptcha";
import ContactWidget from '../components/ContactWidget';
import Footer from '../components/Footer';
import Navbar from '../components/navBar';

export default function Contact() {
    const [formData, setFormData] = useState({
        name: '',
        email: '',
        subject: '',
        message: ''
    });
    
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [submitted, setSubmitted] = useState(false);
    const [captchaValue, setCaptchaValue] = useState(null);
    const [captchaVerified, setCaptchaVerified] = useState(false);
    const [captchaError, setCaptchaError] = useState(false);
    const [verificationStatus, setVerificationStatus] = useState('');
    const recaptchaRef = useRef(null);
    
    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: value
        }));
    };
    
    // Simulated reCAPTCHA verification - in production this should be done server-side
    const simulateVerifyCaptcha = async (token) => {
        setVerificationStatus('verifying');
        
        // Simulate API call delay
        await new Promise(resolve => setTimeout(resolve, 1000));
        
        // In a real implementation, this would be a server call
        // Never include your secret key in frontend code in production!
        if (token && token.length > 0) {
            setCaptchaVerified(true);
            setVerificationStatus('success');
            return true;
        } else {
            setCaptchaVerified(false);
            setVerificationStatus('failed');
            return false;
        }
    };
    
    const handleCaptchaChange = async (token) => {
        setCaptchaValue(token);
        if (token) {
            setCaptchaError(false);
            await simulateVerifyCaptcha(token);
        } else {
            setCaptchaVerified(false);
            setVerificationStatus('');
        }
    };
    
    const handleSubmit = async (e) => {
        e.preventDefault();
        
        // Verify captcha before submission
        if (!captchaVerified) {
            setCaptchaError(true);
            return;
        }
        
        setIsSubmitting(true);
        setCaptchaError(false);
        
        // Simulate form submission
        setTimeout(() => {
            setIsSubmitting(false);
            setSubmitted(true);
            setFormData({
                name: '',
                email: '',
                subject: '',
                message: ''
            });
            setCaptchaVerified(false);
            setCaptchaValue(null);
            setVerificationStatus('');
            
            // Reset reCAPTCHA
            if (recaptchaRef.current) {
                recaptchaRef.current.reset();
            }
            
            // Reset success message after 5 seconds
            setTimeout(() => {
                setSubmitted(false);
            }, 5000);
        }, 1500);
    };
    
    return (
        <div className="min-h-screen bg-white dark:bg-gray-900 text-gray-800 dark:text-gray-200 transition-colors duration-300">
            <Navbar />
            
            <main className="container mx-auto px-4 py-32">
                <div className="max-w-6xl mx-auto">
                    <div className="text-center mb-16">
                        <h1 className="text-4xl md:text-5xl font-bold mb-6 bg-gradient-to-r from-[#8f00ff] to-[#ffd700] bg-clip-text text-transparent">
                            Get In Touch
                        </h1>
                        <div className="w-24 h-1.5 bg-gradient-to-r from-[#8f00ff] to-[#ffd700] rounded-full mb-6 mx-auto"></div>
                        <p className="text-xl text-gray-600 dark:text-gray-300 max-w-3xl mx-auto">
                            Have a project in mind or just want to say hello? I'd love to hear from you!
                        </p>
                    </div>
                    
                    <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 mb-16">
                        <div className="bg-gray-50 dark:bg-gray-800 p-8 rounded-xl shadow-sm flex flex-col items-center text-center transition-all duration-300 hover:shadow-md">
                            <div className="w-14 h-14 bg-[#8f00ff]/10 dark:bg-[#8f00ff]/20 rounded-full flex items-center justify-center mb-4">
                                <FontAwesomeIcon icon={faEnvelope} className="text-[#8f00ff] dark:text-[#ffd700] text-2xl" />
                            </div>
                            <h3 className="text-xl font-semibold mb-2">Email</h3>
                            <p className="text-gray-600 dark:text-gray-400 mb-3">Feel free to email me anytime</p>
                            <a href="mailto:olawale@ola-hub.com" className="text-[#8f00ff] dark:text-[#ffd700] hover:underline">
                                olawalefilani112@gmail.com
                            </a>
                        </div>
                        
                        <div className="bg-gray-50 dark:bg-gray-800 p-8 rounded-xl shadow-sm flex flex-col items-center text-center transition-all duration-300 hover:shadow-md">
                            <div className="w-14 h-14 bg-[#8f00ff]/10 dark:bg-[#8f00ff]/20 rounded-full flex items-center justify-center mb-4">
                                <FontAwesomeIcon icon={faLocationDot} className="text-[#8f00ff] dark:text-[#ffd700] text-2xl" />
                            </div>
                            <h3 className="text-xl font-semibold mb-2">Location</h3>
                            <p className="text-gray-600 dark:text-gray-400 mb-3">Based in</p>
                            <span className="text-gray-700 dark:text-gray-300">
                                Birmingham, United Kingdom
                            </span>
                        </div>
                        
                        <div className="bg-gray-50 dark:bg-gray-800 p-8 rounded-xl shadow-sm flex flex-col items-center text-center transition-all duration-300 hover:shadow-md">
                            <div className="w-14 h-14 bg-[#8f00ff]/10 dark:bg-[#8f00ff]/20 rounded-full flex items-center justify-center mb-4">
                                <FontAwesomeIcon icon={faPhone} className="text-[#8f00ff] dark:text-[#ffd700] text-2xl" />
                            </div>
                            <h3 className="text-xl font-semibold mb-2">Social</h3>
                            <p className="text-gray-600 dark:text-gray-400 mb-3">Let's connect</p>
                            <div className="flex space-x-4">
                                <a href="https://github.com/olawale021" target="_blank" rel="noopener noreferrer" className="text-gray-500 hover:text-[#8f00ff] dark:hover:text-[#ffd700] transition-colors">
                                    <FontAwesomeIcon icon={faGithub} className="text-xl" />
                                </a>
                                <a href="#" target="_blank" rel="noopener noreferrer" className="text-gray-500 hover:text-[#8f00ff] dark:hover:text-[#ffd700] transition-colors">
                                    <FontAwesomeIcon icon={faLinkedin} className="text-xl" />
                                </a>
                                <a href="#" target="_blank" rel="noopener noreferrer" className="text-gray-500 hover:text-[#8f00ff] dark:hover:text-[#ffd700] transition-colors">
                                    <FontAwesomeIcon icon={faTwitter} className="text-xl" />
                                </a>
                            </div>
                        </div>
                    </div>
                    
                    <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-md overflow-hidden transition-all duration-300">
                        <div className="grid grid-cols-1 lg:grid-cols-2">
                            <div className="p-8 lg:p-12">
                                <h2 className="text-3xl font-bold mb-6 text-gray-800 dark:text-white">Send me a message</h2>
                                <p className="text-gray-600 dark:text-gray-300 mb-8">
                                    I'll get back to you as soon as possible.
                                </p>
                                
                                {submitted ? (
                                    <div className="bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 text-green-700 dark:text-green-300 p-4 rounded-lg mb-6">
                                        Thank you for your message! I'll respond as soon as possible.
                                    </div>
                                ) : null}
                                
                                <form onSubmit={handleSubmit}>
                                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-4">
                                        <div>
                                            <label htmlFor="name" className="block text-gray-700 dark:text-gray-300 mb-2">Name</label>
                                            <input
                                                type="text"
                                                id="name"
                                                name="name"
                                                value={formData.name}
                                                onChange={handleChange}
                                                required
                                                className="w-full px-4 py-3 border border-gray-300 dark:border-gray-700 rounded-lg focus:ring-2 focus:ring-[#8f00ff] dark:focus:ring-[#ffd700] focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-white transition-colors duration-200"
                                                placeholder="Your name"
                                            />
                                        </div>
                                        <div>
                                            <label htmlFor="email" className="block text-gray-700 dark:text-gray-300 mb-2">Email</label>
                                            <input
                                                type="email"
                                                id="email"
                                                name="email"
                                                value={formData.email}
                                                onChange={handleChange}
                                                required
                                                className="w-full px-4 py-3 border border-gray-300 dark:border-gray-700 rounded-lg focus:ring-2 focus:ring-[#8f00ff] dark:focus:ring-[#ffd700] focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-white transition-colors duration-200"
                                                placeholder="Your email"
                                            />
                                        </div>
                                    </div>
                                    
                                    <div className="mb-4">
                                        <label htmlFor="subject" className="block text-gray-700 dark:text-gray-300 mb-2">Subject</label>
                                        <input
                                            type="text"
                                            id="subject"
                                            name="subject"
                                            value={formData.subject}
                                            onChange={handleChange}
                                            required
                                            className="w-full px-4 py-3 border border-gray-300 dark:border-gray-700 rounded-lg focus:ring-2 focus:ring-[#8f00ff] dark:focus:ring-[#ffd700] focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-white transition-colors duration-200"
                                            placeholder="Message subject"
                                        />
                                    </div>
                                    
                                    <div className="mb-6">
                                        <label htmlFor="message" className="block text-gray-700 dark:text-gray-300 mb-2">Message</label>
                                        <textarea
                                            id="message"
                                            name="message"
                                            value={formData.message}
                                            onChange={handleChange}
                                            required
                                            rows="6"
                                            className="w-full px-4 py-3 border border-gray-300 dark:border-gray-700 rounded-lg focus:ring-2 focus:ring-[#8f00ff] dark:focus:ring-[#ffd700] focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-white transition-colors duration-200 resize-none"
                                            placeholder="Your message"
                                        ></textarea>
                                    </div>
                                    
                                    <div className="mb-6">
                                        <div className="dark:bg-gray-100 inline-block p-2 rounded-lg">
                                            <ReCAPTCHA
                                                ref={recaptchaRef}
                                                sitekey="6LdqzBorAAAAADU6F0q1wa4oKZDJK2Rd-1mN0_RF"
                                                onChange={handleCaptchaChange}
                                                theme="light"
                                            />
                                        </div>
                                        
                                        {verificationStatus === 'verifying' && (
                                            <div className="flex items-center mt-2 text-blue-600 dark:text-blue-400">
                                                <svg className="animate-spin -ml-1 mr-2 h-4 w-4" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                                                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                                </svg>
                                                Verifying...
                                            </div>
                                        )}
                                        
                                        {verificationStatus === 'success' && (
                                            <div className="flex items-center mt-2 text-green-600 dark:text-green-400">
                                                <svg className="w-5 h-5 mr-1" fill="currentColor" viewBox="0 0 20 20" xmlns="http://www.w3.org/2000/svg">
                                                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd"></path>
                                                </svg>
                                                Verification successful
                                            </div>
                                        )}
                                        
                                        {verificationStatus === 'failed' && (
                                            <div className="flex items-center mt-2 text-red-600 dark:text-red-400">
                                                <svg className="w-5 h-5 mr-1" fill="currentColor" viewBox="0 0 20 20" xmlns="http://www.w3.org/2000/svg">
                                                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd"></path>
                                                </svg>
                                                Verification failed. Please try again.
                                            </div>
                                        )}
                                        
                                        {captchaError && (
                                            <p className="text-red-500 mt-2">Please verify that you are not a robot.</p>
                                        )}
                                        
                                        <p className="text-sm text-gray-500 dark:text-gray-400 mt-2">
                                            This site is protected by reCAPTCHA to ensure you're not a robot.
                                        </p>
                                    </div>
                                    
                                    <button
                                        type="submit"
                                        disabled={isSubmitting || !captchaVerified}
                                        className={`px-6 py-3 rounded-lg bg-gradient-to-r from-[#8f00ff] to-[#ffd700] text-white font-medium transition-all duration-300 transform hover:scale-[1.02] hover:shadow-lg flex items-center justify-center ${isSubmitting || !captchaVerified ? 'opacity-70 cursor-not-allowed' : ''}`}
                                    >
                                        {isSubmitting ? (
                                            <>
                                                <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                                                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                                </svg>
                                                Sending...
                                            </>
                                        ) : 'Send Message'}
                                    </button>
                                </form>
                            </div>
                            
                            <div className="bg-gradient-to-br from-[#8f00ff] to-[#ffd700] hidden lg:block relative overflow-hidden">
                                <div className="absolute inset-0 bg-black opacity-20"></div>
                                <div className="absolute inset-0 flex flex-col items-center justify-center p-12 text-white">
                                    <h3 className="text-3xl font-bold mb-4">Let's create something amazing together</h3>
                                    <p className="text-white/80 mb-8 text-center">
                                        Whether you need a website, mobile app, or digital solution, I'm here to help bring your ideas to life.
                                    </p>
                                    <div className="w-24 h-1 bg-white rounded-full mb-8"></div>
                                    <div className="grid grid-cols-3 gap-6 w-full max-w-xs">
                                        <a href="https://github.com/olawale021" target="_blank" rel="noopener noreferrer" className="w-12 h-12 rounded-full bg-white/20 flex items-center justify-center hover:bg-white/30 transition-colors">
                                            <FontAwesomeIcon icon={faGithub} className="text-white text-xl" />
                                        </a>
                                        <a href="#" target="_blank" rel="noopener noreferrer" className="w-12 h-12 rounded-full bg-white/20 flex items-center justify-center hover:bg-white/30 transition-colors">
                                            <FontAwesomeIcon icon={faLinkedin} className="text-white text-xl" />
                                        </a>
                                        <a href="#" target="_blank" rel="noopener noreferrer" className="w-12 h-12 rounded-full bg-white/20 flex items-center justify-center hover:bg-white/30 transition-colors">
                                            <FontAwesomeIcon icon={faTwitter} className="text-white text-xl" />
                                        </a>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </main>
            
            <ContactWidget />
            <Footer />
        </div>
    );
} 